// Thin wrapper over aws-rum-web's AwsRum client (CloudWatch RUM), installed
// as an npm dependency rather than AWS's CDN <script> snippet so it stays
// covered by the existing `script-src 'self'` CSP - see
// infra/lib/kanalearn-rum-stack.ts for the App Monitor/Identity Pool this
// talks to.
//
// Safe no-op (never constructs an AwsRum client) unless all three of
// REACT_APP_RUM_APP_MONITOR_ID / REACT_APP_RUM_IDENTITY_POOL_ID /
// REACT_APP_RUM_REGION are set, exactly like ga4.ts/posthog.ts no-op
// without their own config - which is the actual state of every .env file
// in this repo today, since kanalearn-rum-stack.ts hasn't been deployed
// yet.
//
// Telemetry collected: page views, performance metrics, JS errors and HTTP
// errors, plus RUM's own automatic session/device/browser collection -
// exactly the `telemetries` set configured server-side in
// appMonitorConfiguration (kanalearn-rum-stack.ts). On top of that, every
// custom product event already sent through ../index.ts's track() (e.g.
// lesson_started, quiz_completed) is also forwarded here via recordEvent()
// - see track() in ../index.ts.
//
// Page views specifically are NOT wired through the page()/track() facade
// in ../index.ts: aws-rum-web patches history.pushState/replaceState and
// listens for popstate by default (`disableAutoPageView` defaults to
// false), so it already observes every react-router-dom navigation on its
// own. Calling recordPageView() from PageViewTracker too would double-count
// every route change, so ../index.ts's page()/identify()/reset()
// intentionally never call into this module for that - init() and, since
// Stage 5, track()'s recordEvent() call are the only things wired up.
//
// Auth: only identityPoolId is configured below, no guestRoleArn (there's
// no such output from kanalearn-rum-stack.ts). aws-rum-web only uses the
// classic AssumeRoleWithWebIdentity flow (which needs guestRoleArn) when
// both identityPoolId AND guestRoleArn are present; with identityPoolId
// alone it falls back to the enhanced GetCredentialsForIdentity flow, which
// resolves credentials via whichever role the identity pool already has
// attached - exactly the unauthenticated role kanalearn-rum-stack.ts wires
// up with CfnIdentityPoolRoleAttachment. No frontend change needed if a
// guestRoleArn output is added later; it would just switch which flow is
// used.
//
// Consent: gated the same way PostHog is (see setConsent() in
// providers/posthog.ts), just with a different underlying API. aws-rum-web's
// AwsRum/Orchestration class (see node_modules/@aws-rum/web-slim's
// Orchestration.js) exposes real enable()/disable() methods that go deeper
// than "stop sending" - disable() turns off the Dispatch (no network
// requests), the PluginManager (unpatches history.pushState/replaceState so
// route changes aren't even observed) and the EventCache (recordEvent/
// recordCandidate become no-ops), i.e. it stops collection at the source,
// not just transmission. The config's `enableRumClient: false` below starts
// the client in that disabled state at construction (this is aws-rum-web's
// own documented flag for exactly this "wait for cookie consent" case -
// Orchestration's constructor calls disable() when it sees
// enableRumClient: false); setConsent() then calls enable()/disable() as
// consent changes, called from ../index.ts's onConsentChange handler,
// mirroring posthogProvider.setConsent(). No client is constructed at all
// until init() runs (from src/index.tsx), so there's nothing to gate before
// then.
import { AwsRum } from "aws-rum-web";
import type { AwsRumConfig } from "aws-rum-web";

const APP_MONITOR_ID = process.env.REACT_APP_RUM_APP_MONITOR_ID;
const IDENTITY_POOL_ID = process.env.REACT_APP_RUM_IDENTITY_POOL_ID;
const REGION = process.env.REACT_APP_RUM_REGION;

// Informational only (shown in the CloudWatch RUM console) - not tied to
// package.json's version since that isn't exposed to the frontend bundle.
const APPLICATION_VERSION = "1.0.0";

let client: AwsRum | undefined;
let initialized = false;

export function init(): void {
    if (initialized) return;
    initialized = true;
    if (!APP_MONITOR_ID || !IDENTITY_POOL_ID || !REGION) return;

    // Keep sessionSampleRate/telemetries/allowCookies/enableXRay in sync
    // with appMonitorConfiguration in infra/lib/kanalearn-rum-stack.ts - the
    // npm-installed client doesn't fetch that config remotely, it has to be
    // duplicated here.
    //
    // allowCookies stays true unconditionally (not itself toggled by
    // consent): it only controls whether aws-rum-web is *permitted* to
    // write its own session/user cookies, which it only actually does from
    // inside the same recordEvent/session-tracking path that
    // enableRumClient/disable() already turns off. So pre-consent, no RUM
    // cookies get written regardless of this flag; it only takes effect
    // once enable() has been called.
    const config: AwsRumConfig = {
        sessionSampleRate: 0.1,
        identityPoolId: IDENTITY_POOL_ID,
        endpoint: `https://dataplane.rum.${REGION}.amazonaws.com`,
        telemetries: ["errors", "performance", "http"],
        allowCookies: true,
        enableXRay: false,
        // Start fully disabled - no session start, no page views, no
        // errors/http collection - until setConsent(true) is called. See
        // the file-level comment above for what disable()/enable() actually
        // toggle.
        enableRumClient: false,
    };

    try {
        client = new AwsRum(APP_MONITOR_ID, APPLICATION_VERSION, REGION, config);
    } catch (error) {
        // Mirrors AWS's own documented setup snippet: never let a RUM
        // client failure (e.g. blocked by an ad blocker, malformed config)
        // break the app.
        console.warn("[analytics] CloudWatch RUM failed to initialize", error);
    }
}

// Called from ../index.ts's onConsentChange handler (and once immediately
// from initRum() to apply whatever consent state is already known),
// mirroring posthogProvider.setConsent(). A no-op if init() was never
// called or never successfully constructed a client (e.g. RUM env vars
// unset, or construction threw).
export function setConsent(granted: boolean): void {
    if (!client) return;
    if (granted) {
        client.enable();
    } else {
        client.disable();
    }
}

// Called from ../index.ts's track() so every product event that already
// reaches GA4/PostHog also reaches CloudWatch RUM as a custom event, via
// AwsRum/Orchestration's own recordEvent(eventType, eventData, metadata?)
// method (see node_modules/@aws-rum/web-slim's Orchestration.d.ts) - a
// no-op if no client was constructed (RUM env vars unset) or if consent
// hasn't been granted yet (disable() turns the EventCache's recordEvent
// into a no-op at the source, per the file-level comment above).
export function recordEvent(name: string, properties: Record<string, unknown> = {}): void {
    if (!client) return;
    try {
        client.recordEvent(name, properties);
    } catch (error) {
        console.warn("[analytics] CloudWatch RUM failed to record event", name, error);
    }
}

// Test-only escape hatch, mirrors the pattern in consent.ts.
export function __getClientForTests(): AwsRum | undefined {
    return client;
}
